import update from 'immutability-helper';
import { AnyAction } from 'redux';

import { SHOW_TRADFI_PRODUCTS } from 'constants/constants';
import { API_STATUS } from 'constants/enums';
import { getDateTime } from 'helpers/day';
import { find, values, path, pluck, map, replace, set, lensPath } from 'helpers/ramda';
import { getContractType, isTradFiContract } from 'helpers/utils';
import { Product, SpotIndex } from 'types/IProducts';
import { ISparkLineData, ISparkLines } from 'types/ITrade';

import { RECONNECT_SOCKET, RECONNECT_SOCKET_TAB_REACTIVE } from '../actionTypes/socket';
import TRADE, { TRADE_CONSTANTS } from '../actionTypes/trade';
import USER, {
  UNAUTHORIZED,
  USER_CONNECTED,
  USER_DISCONNECTED,
} from '../actionTypes/user';
import { reduceById } from '../helpers/reduceByProp';
import { pathToString, processWithdrawalState } from './helpers/trade';
import initialState from './initialstate/trade';

// eslint-disable-next-line default-param-last
const trade = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case UNAUTHORIZED:
    case USER.LOGOUT.SUCCESS: {
      return {
        ...state,
        open_positions: {},
      };
    }

    case TRADE.ASSETS.SUCCESS: {
      const assetsList = action.result.result;
      return { ...state, assets: reduceById(assetsList) };
    }

    case TRADE.DEPOSIT_ASSETS.LOAD: {
      return {
        ...state,
        depositAssetsApiStatus: API_STATUS.LOADING,
      };
    }

    case TRADE.DEPOSIT_ASSETS.SUCCESS: {
      const depositAssetsList = action.result.result;
      return {
        ...state,
        depositAssets: reduceById(depositAssetsList),
        depositAssetsApiStatus: API_STATUS.SUCCESS,
      };
    }

    case TRADE.DEPOSIT_ASSETS.FAIL: {
      return {
        ...state,
        depositAssetsApiStatus: API_STATUS.ERROR,
      };
    }

    case TRADE_CONSTANTS.ASSET_SELECTED: {
      const selectedAssetId = action.payload.toString();
      const selectedProduct = find(
        item => pathToString(['underlying_asset', 'id'], item) === selectedAssetId,
        values(state.products)
      );
      const selectedAsset = path(['assets', selectedAssetId], state);
      return {
        ...state,
        selected_product: selectedProduct,
        selected_asset: selectedAsset,
      };
    }

    case TRADE_CONSTANTS.PRODUCT_SELECTED: {
      const selectedProductId = String(action.payload);
      if (selectedProductId) {
        const contractType: string = path(
          ['products', selectedProductId, 'contract_type'],
          state
        );

        return {
          ...state,
          selected_product: path(['products', selectedProductId], state),
          previousSelectedProductId: state.selected_product
            ? state.selected_product.id
            : null,
          selectedContractType:
            window.location.pathname.indexOf('/options_chain') > -1
              ? 'options_chain'
              : getContractType(contractType),
        };
      }
      return state;
    }

    case TRADE_CONSTANTS.CHANGE_CONTRACT_TYPE: {
      const contractType = action.payload;
      return {
        ...state,
        selectedContractType: contractType,
      };
    }

    case TRADE.PRODUCTS.SUCCESS: {
      const productsList: Product[] = action.result.result;
      // NOTE: CURRENTLY WE DON NOT WANT TO SHOW TRADFI PRODUCTS IN INDIAN_EXCHANGE, BUT BE APIs STILL RETURN THEM.
      // TODO: ONCE THE BE API IS FIXED/WE DECIDE TO SHOW TRADFI PRODUCTS IN INDIAN_EXCHANGE, REMOVE THIS FILTER

      // Store TradFi product names before filtering for use in universal search
      const tradFiProductNames = productsList
        .filter(product => isTradFiContract(product))
        .map(product => product.symbol);

      const filteredProductsList = productsList.filter(
        product => !isTradFiContract(product)
      );
      const shownProductList = SHOW_TRADFI_PRODUCTS ? productsList : filteredProductsList;
      const products: { [key: string]: Product } = reduceById<Product>(shownProductList);
      // Moved orderbook to react easy state
      const productNames = pluck('symbol', shownProductList).join(',');

      return {
        ...state,
        products,
        product_names: productNames,
        tradFiProductNames,
        productsLoaded: true,
      };
    }

    case TRADE.SPOT_INDICES.SUCCESS: {
      const spotIndices = {};
      action?.result?.result?.forEach((obj: SpotIndex) => {
        spotIndices[obj.id] = obj;
      });
      return {
        ...state,
        spot_indices: spotIndices,
      };
    }

    case TRADE_CONSTANTS.UPDATE_PRODUCT: {
      const product: Product = action.payload;
      const productId: string = product.id.toString();
      return update(state, {
        products: {
          [productId]: {
            $set: product,
          },
        },
        selected_product: {
          $apply(currentSelectedProduct: Product) {
            if (
              currentSelectedProduct &&
              currentSelectedProduct.id.toString() === productId
            ) {
              return product;
            }
            return currentSelectedProduct;
          },
        },
      });
    }

    case TRADE.L2ORDERBOOK.LOAD:
    case TRADE.L2ORDERBOOK.FAIL: {
      return {
        ...state,
        loadingOrderbook: true,
      };
    }

    case TRADE.WITHDRAWAL_DATA.SUCCESS: {
      const { result } = action.result;
      const pendingWithdrawals = {
        time: getDateTime(result.created_at),
        address: result.address,
        amount: result.amount,
        fee: result.fee,
        id: result.id,
        transaction_id: result.transaction_id,
        network: result.network,
        asset_symbol: result.asset_symbol,
        ...processWithdrawalState(result),
      };

      return update(state, {
        withdrawalFailed: {
          $set: false,
        },
        pendingWithdrawals: {
          $unshift: [pendingWithdrawals],
        },
      });
    }

    case TRADE.WITHDRAWAL_DATA.FAIL:
      return {
        ...state,
        withdrawalFailed: true,
        withdrawalFailedStatus: action.error.status,
      };

    case TRADE.PENDING_WITHDRAWAL_DATA.SUCCESS: {
      const pendingWithdrawals = map(
        item => ({
          asset_symbol: item.asset_symbol,
          time: getDateTime(item.created_at),
          address: item.address,
          amount: item.amount,
          fee: item.fee,
          state: item.state,
          id: item.id,
          transaction_id: item.transaction_id,
          network: item.network,
          ...processWithdrawalState(item),
        }),
        action.result.result
      );
      return {
        ...state,
        pendingWithdrawals,
      };
    }

    case TRADE.WITHDRAWAL_LIMITS.SUCCESS: {
      const data = action.result.result;
      const withdrawalLimits = {
        bonus: data.trading_credits,
        blocked_amount: data.blocked_amount,
        max_withdrawal_after_forfeiting: data.max_withdrawal_after_forfeiting,
        max_withdrawal_without_forfeiting: data.max_withdrawal_without_forfeiting,
        deto_withdrawal_fee_discount_enabled: data.deto_withdrawal_fee_discount_enabled,
      };
      return {
        ...state,
        withdrawalLimits,
      };
    }

    case TRADE.WITHDRAWAL_TRADING_CREDITS.SUCCESS: {
      const data = action.result.result;
      const assets = {};

      data.forEach(obj => {
        if (!assets[obj.asset_id]) {
          assets[obj.asset_id] = {
            amount: 0,
          };
        }
        assets[obj.asset_id].amount += parseFloat(obj.amount);
      });
      return {
        ...state,
        tradingCredits: assets,
      };
    }

    case TRADE.WITHDRAWAL_LIMITS.FAIL: {
      return state;
    }

    case TRADE.GET_FIAT_CURRENCIES.SUCCESS: {
      const currencyList = action.result.result;
      const filteredFiatCurrencyList = currencyList.filter(
        currencyObj => currencyObj.isAllowed
      );

      return {
        ...state,
        fiatCurrenciesList: filteredFiatCurrencyList,
      };
    }

    case TRADE.GET_FIAT_DEPOSIT_ASSETS.SUCCESS: {
      return {
        ...state,
        fiatDepositAssetsList: action.result.result,
      };
    }

    case TRADE_CONSTANTS.RESET_FIAT_DEPOSIT_ASSETS: {
      return {
        ...state,
        fiatDepositAssetsList: [],
      };
    }

    case TRADE.GET_CRYPTO_FIAT_CONVERSION.SUCCESS: {
      return {
        ...state,
        fiatCryptoConversionData: action.result.result,
      };
    }

    case TRADE.SPARKLINES.SUCCESS: {
      const sparklinedata: ISparkLineData = action.result;
      const convertArrayToObject = ([time, value]: [number, number]) => ({
        time,
        value,
      });
      const sparklines: ISparkLines = map(map(convertArrayToObject), sparklinedata);

      return {
        ...state,
        sparklines,
      };
    }

    case TRADE.CHART_HISTORY.SUCCESS: {
      const { params, result } = action;
      const symbol = replace('FUNDING:', '', params.symbol);
      const chartHistoryLens = lensPath(['chartHistory', symbol, params.type]);
      return set(chartHistoryLens, result, state);
    }

    case TRADE.ORDER.LOAD:
    case TRADE.CLOSE_POSITION.LOAD:
      return {
        ...state,
        orderLoading: true,
      };

    case TRADE.ORDER.SUCCESS:
    case TRADE.ORDER.FAIL:
    case TRADE.CLOSE_POSITION.SUCCESS:
    case TRADE.CLOSE_POSITION.FAIL:
      return {
        ...state,
        orderLoading: false,
      };

    case USER_CONNECTED:
      return {
        ...state,
        isOnline: true,
        offlineTime: null,
      };

    case USER_DISCONNECTED:
      return {
        ...state,
        isOnline: false,
        offlineTime: state.isOnline ? new Date().getTime() : state.offlineTime,
      };

    case RECONNECT_SOCKET:
      return {
        ...state,
        isSocketReconnecting: true,
        socketDisconnectTime: !state.isSocketReconnecting
          ? new Date().getTime()
          : state.socketDisconnectTime,
      };

    case RECONNECT_SOCKET_TAB_REACTIVE:
      return {
        ...state,
        isSocketReconnecting: true,
        socketDisconnectTime: !state.isSocketReconnecting
          ? new Date().getTime()
          : state.socketDisconnectTime,
      };

    case TRADE.GET_STATISTICS.SUCCESS:
      return {
        ...state,
        stats: action.result.result,
      };

    case TRADE.ALL_TICKERS.SUCCESS: {
      const futuresVolumes = {};
      action.result?.result?.forEach(ticker => {
        if (ticker.symbol) {
          futuresVolumes[ticker.symbol] = ticker.turnover_usd ?? 0;
        }
      });

      return {
        ...state,
        futuresVolumes,
      };
    }

    default:
      return state;
  }
};

export default trade;
